import {VisuallyHidden} from '@commercial-helios/ui/src/components/visually-hidden'
import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

type TableDataRow = {
    heading: string
    data1: string
    data2: string
}

type TableData = {
    column1Heading: string
    column2Heading: string
    rows: TableDataRow[]
}

export type Props = {
    heading: string
    subheading?: string
    tableData: TableData
}

export const ComparisonTable = ({heading, subheading, tableData}: Props) => (
    <Wrapper>
        <Headings>
            <Text as="h2" variant="heading-base">
                {heading}
            </Text>

            {subheading && (
                <Text variant="title-base" color="muted">
                    {subheading}
                </Text>
            )}
        </Headings>

        <Table>
            <VisuallyHidden as="caption">{heading}</VisuallyHidden>

            <TBody>
                {tableData.rows.map((row, i) => (
                    <TR key={i}>
                        <TH scope="row">
                            <Text variant="title-base">{row.heading}</Text>
                        </TH>

                        <TD>
                            <Tag $color="pink">
                                <Text as="span" variant="label-base" color="muted">
                                    {tableData.column1Heading}
                                </Text>
                            </Tag>

                            <Text variant="paragraph-large" color="muted">
                                {row.data1}
                            </Text>
                        </TD>

                        <TD>
                            <Tag $color="grey">
                                <Text as="span" variant="label-base" color="muted">
                                    {tableData.column2Heading}
                                </Text>
                            </Tag>

                            <Text variant="paragraph-large" color="muted">
                                {row.data2}
                            </Text>
                        </TD>
                    </TR>
                ))}
            </TBody>
        </Table>
    </Wrapper>
)

const Wrapper = styled(Section)`
    display: grid;
    row-gap: ${tokens.spacing40};

    ${mq.min('lg')} {
        row-gap: ${tokens.spacing60};
    }
`

const Headings = styled.div`
    display: grid;
    row-gap: ${tokens.spacing12};

    ${mq.min('md')} {
        row-gap: ${tokens.spacing20};
    }
`

const Table = styled.table`
    display: block;
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;
`

const TBody = styled.tbody`
    display: block;
    width: 100%;
`

const TR = styled.tr`
    display: grid;
    grid-template-columns: 1fr;
    row-gap: ${tokens.spacing20};

    & + & {
        margin-top: ${tokens.spacing40};
    }

    ${mq.min('md')} {
        grid-template-columns: repeat(2, 1fr);
        column-gap: ${tokens.spacing40};
        row-gap: ${tokens.spacing40};
    }

    ${mq.min('lg')} {
        grid-template-columns: 280px 1fr 1fr;
        column-gap: 80px;
        padding-top: ${tokens.spacing40};
        border-top: 1px solid ${colors.border.dark};
    }
`

const TD = styled.td`
    display: grid;
    justify-items: start;
    align-content: start;
    row-gap: ${tokens.spacing8};
`

const TH = styled.th`
    text-align: left;

    ${mq.max('md')} {
        padding-bottom: ${tokens.spacing8};
    }

    ${mq.between('md', 'lg')} {
        grid-column: 1 / -1;
        padding-bottom: ${tokens.spacing12};
    }

    ${mq.max('lg')} {
        border-bottom: 1px solid ${colors.border.dark};
    }
`

export type TagProps = {
    $color: keyof typeof colors.tag
}

const Tag = styled.div<TagProps>`
    padding: ${tokens.spacing4} ${tokens.spacing8};
    background: ${(props) => colors.tag[props.$color]};
    border-radius: ${radii.arc4};
`

export const comparisonTableFragment = graphql`
    fragment ComparisonTable on PrismicSlicePageDataBodyComparisonTable {
        __typename
        primary {
            section_heading
            section_subheading
            column_1_heading
            column_2_heading
        }
        items {
            heading
            data_1
            data_2
        }
    }
`
